import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type EnrichmentCreditsResult,
  type PreviewMiningResult,
} from '@whinerp/client';
import { Button, Num, Panel, SectionLabel, Skeleton, ErrorState } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * Finding companies nobody here knows yet (F-154).
 *
 * The specification's warning is the design: "show the cost of the selection
 * before the import, not after. Credit-metered features that surprise on the
 * bill do not get used twice."
 *
 * So the cost of what is ticked is on screen, next to the button, and it
 * changes as the selection changes. Looking is free and says so; a row already
 * in the workspace is ticked out of the way and cannot be selected, because it
 * would cost nothing and do nothing.
 */
export function MiningPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [industry, setIndustry] = useState('');
  const [geography, setGeography] = useState('');
  const [role, setRole] = useState('');
  const [preview, setPreview] = useState<PreviewMiningResult | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());

  const credits = useQuery<EnrichmentCreditsResult, ApiError>({
    queryKey: ['enrichment-credits'],
    queryFn: () => api.enrichmentCredits({}),
  });

  const criteria = (): { industry?: string; geography?: string; role?: string; wanted: number } => ({
    ...(industry.trim() === '' ? {} : { industry: industry.trim() }),
    ...(geography.trim() === '' ? {} : { geography: geography.trim() }),
    ...(role.trim() === '' ? {} : { role: role.trim() }),
    wanted: 10,
  });

  const look = useMutation<PreviewMiningResult, ApiError>({
    mutationFn: () => api.previewMining({ body: criteria() }),
    onSuccess: (result) => {
      setPreview(result);
      // Everything new, ticked. A select-all somebody has to press before they
      // can see a cost is a select-all that hides the cost.
      setChosen(new Set(result.candidates.filter((row) => !row.known).map((row) => row.domain)));
    },
  });

  const bring = useMutation<unknown, ApiError>({
    mutationFn: () => api.importMined({ body: { ...criteria(), domains: [...chosen] } }),
    onSuccess: async () => {
      setPreview(null);
      setChosen(new Set());
      await queryClient.invalidateQueries({ queryKey: ['enrichment-credits'] });
    },
  });

  if (credits.isPending) return <Skeleton className="h-block-md w-full" />;
  /*
   * A failure is not a loading state.
   *
   * These returned a Skeleton, so a panel whose request failed showed a
   * shimmer for ever - a screen that never loads rather than one that says
   * what went wrong, and a `settled()` that never settles (D-365).
   */
  if (credits.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={credits.error.message}
        onRetry={() => void credits.refetch()}
      />
    );
  }

  const failure = look.error ?? bring.error;
  // Counted from what is ticked, not from what was returned: the number beside
  // the button has to be the number the button will spend.
  const cost = [...chosen].length;

  return (
    <Panel data-region="mining" id="mining">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('mining.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('mining.body')}</p>
          <p data-mining-balance className="text-dense text-ink">
            <Num>
              {t('mining.balance', {
                values: { balance: formatCount(credits.data.balance, format) },
              })}
            </Num>
          </p>
          {credits.data.provider.mode !== 'live' ? (
            <p data-mining-mode={credits.data.provider.mode} className="text-meta text-ink-meta">
              {credits.data.provider.nextStep}
            </p>
          ) : null}
        </div>

        {failure ? (
          <p
            data-mining-refusal
            className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text"
          >
            {failure.message}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-2">
          {(
            [
              ['industry', industry, setIndustry],
              ['geography', geography, setGeography],
              ['role', role, setRole],
            ] as const
          ).map(([key, value, set]) => (
            <label key={key} className="flex min-w-0 flex-1 flex-col gap-1 text-dense text-ink-600">
              {t(`mining.${key}` as 'mining.industry')}
              <input
                data-mining-field={key}
                className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink"
                value={value}
                onChange={(event) => set(event.target.value)}
              />
            </label>
          ))}
          {/* Looking is free, and the label says so. */}
          <Button intent="secondary" data-mining-look onClick={() => look.mutate()}>
            {t('mining.look')}
          </Button>
        </div>

        {preview ? (
          <div className="flex flex-col gap-2">
            <p data-mining-found className="text-dense text-ink-600">
              <Num>
                {t('mining.found', {
                  values: {
                    found: formatCount(preview.found, format),
                    known: formatCount(preview.found - preview.importable, format),
                  },
                })}
              </Num>
            </p>

            <div data-scroll-container className="overflow-x-auto">
              <table className="w-full border-collapse text-dense">
                <tbody>
                  {preview.candidates.map((row) => (
                    <tr
                      key={row.domain}
                      data-mining-row={row.domain}
                      data-mining-known={row.known ? 'yes' : 'no'}
                      className="border-t border-t-rule"
                    >
                      <td className="px-2 py-1">
                        <input
                          type="checkbox"
                          data-mining-pick={row.domain}
                          // A company already here cannot be chosen: it would
                          // cost nothing and do nothing, and a tick that does
                          // nothing is a control wired to nothing.
                          disabled={row.known}
                          checked={chosen.has(row.domain)}
                          onChange={(event) => {
                            const next = new Set(chosen);
                            if (event.target.checked) next.add(row.domain);
                            else next.delete(row.domain);
                            setChosen(next);
                          }}
                          aria-label={row.name}
                        />
                      </td>
                      <td className="px-2 py-1 text-ink">{row.name}</td>
                      <td className="px-2 py-1 text-ink-meta">{row.domain}</td>
                      <td className="px-2 py-1 text-meta text-ink-meta">
                        {/* Where the row came from, on the row. A sandbox
                            company must never be imported as a real business
                            by somebody who did not notice. */}
                        {row.source}
                      </td>
                      <td className="px-2 py-1 text-meta text-ink-meta">
                        {row.known ? (
                          <span data-mining-already>
                            {t('mining.already', { values: { label: row.knownAs?.label ?? '' } })}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* The cost of THIS selection, beside the button that spends it. */}
              <span data-mining-cost={String(cost)} className="text-dense text-ink">
                <Num>
                  {t('mining.cost', {
                    values: {
                      count: formatCount(cost, format),
                      balance: formatCount(credits.data.balance, format),
                    },
                  })}
                </Num>
              </span>
              <Button
                intent="primary"
                size="small"
                data-mining-import
                disabled={cost === 0}
                onClick={() => bring.mutate()}
              >
                {t('mining.import')}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
